import { EmptyPage } from '@/shared/components/EmptyPage';
import { MOBILE_APP_CTA_LABEL, MOBILE_APP_CTA_URL } from '@/shared/constants/urls';
import { Button } from '@/shared/shadcn/ui/button';
import { CheckIcon, CopyIcon, ExternalLinkIcon, MobileIcon } from '@radix-ui/react-icons';
import { useCallback, useState } from 'react';

export type MobileMemoryFallbackReason = 'tooLarge' | 'outOfMemory';

interface Props {
  reason: MobileMemoryFallbackReason;
  sizeBytes?: number;
  limitBytes?: number;
}

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Screen shown to mobile users when a file is too large for their browser's
 * memory limits, or when the core worker has actually run out of memory.
 *
 * Provides a copyable link so the user can reopen the file on desktop, and
 * a direct link to the destination configured in `MOBILE_APP_CTA_URL`. When
 * the native (Cordova) app ships, updating that single constant swaps the
 * destination without touching this component.
 */
export function MobileMemoryFallback({ reason, sizeBytes, limitBytes }: Props) {
  const [copied, setCopied] = useState(false);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopy = useCallback(async () => {
    if (!currentUrl) return;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [currentUrl]);

  const title = reason === 'tooLarge' ? 'File too large for mobile' : 'Out of memory';

  const sizeLine =
    reason === 'tooLarge' && sizeBytes !== undefined && limitBytes !== undefined
      ? `This file is ${formatMb(sizeBytes)}; the mobile browser limit is ${formatMb(limitBytes)}.`
      : reason === 'outOfMemory'
        ? 'Your mobile browser ran out of memory while loading this file.'
        : 'This file is too large to open safely in a mobile browser.';

  const description = (
    <div className="flex flex-col gap-2">
      <p>{sizeLine}</p>
      <p>Open it on {MOBILE_APP_CTA_LABEL} to keep working. The link to this file has been preserved for you below.</p>
    </div>
  );

  const actions = (
    <div className="flex flex-col items-center justify-center gap-2">
      <Button asChild variant="default">
        <a href={MOBILE_APP_CTA_URL} target="_blank" rel="noreferrer">
          <ExternalLinkIcon className="mr-2" />
          Open on {MOBILE_APP_CTA_LABEL}
        </a>
      </Button>
      {currentUrl && (
        <Button variant="outline" onClick={handleCopy}>
          {copied ? (
            <>
              <CheckIcon className="mr-2" />
              Link copied
            </>
          ) : (
            <>
              <CopyIcon className="mr-2" />
              Copy link to this file
            </>
          )}
        </Button>
      )}
    </div>
  );

  return <EmptyPage title={title} description={description} Icon={MobileIcon} actions={actions} />;
}
